import { ReactElement, useId } from 'react'
import { salesReportCopy } from '../copy'
import { formatCents, formatPercent, Gender, genderLabel, TicketType } from '../format'
import { salesReportIcons } from '../icons'
import { occupancyTone, ProgressBar } from '../ProgressBar'
import { StockHint } from '../StockHint'
import { TipoBadge } from '../TipoBadge'
import * as S from './styles'

export interface MobileTicketCardProps {
  ticket: string
  lot: string
  gender: Gender
  ticketType: TicketType
  courtesy: boolean
  quantity: number
  stock?: { text: string, low?: boolean, hint?: string }
  unitValueCents: number
  feeCents?: number
  paymentMethod?: string
  totalCents: number
}

export function MobileTicketCard(props: MobileTicketCardProps): ReactElement {
  return (
    <S.Card>
      <S.Top>
        <S.Identity>
          <S.TicketName>{props.ticket}</S.TicketName>
          <S.TicketMeta>{`Lote: ${props.lot} · ${genderLabel(props.gender)}`}</S.TicketMeta>
        </S.Identity>
        <TipoBadge ticketType={props.ticketType} courtesy={props.courtesy} compact />
      </S.Top>
      <S.Middle>
        <S.QuantityText>{`Qtd: ${props.quantity.toLocaleString('pt-BR')}`}</S.QuantityText>
        {props.stock && (
          <>
            <S.Divider aria-hidden="true" />
            <StockHint
              variant="mobile"
              text={`${salesReportCopy.stockColumn}: ${props.stock.text}`}
              hint={props.stock.hint}
              low={props.stock.low === true}
            />
          </>
        )}
        {props.paymentMethod && (
          <>
            <S.Divider aria-hidden="true" />
            <S.MetaText>{props.paymentMethod}</S.MetaText>
          </>
        )}
      </S.Middle>
      <S.Values>
        <S.ValueBlock>
          <S.ValueLabel>Val. Unit</S.ValueLabel>
          <S.ValueText>{formatCents(props.unitValueCents)}</S.ValueText>
        </S.ValueBlock>
        {props.feeCents != null && (
          <S.ValueBlock>
            <S.ValueLabel>Taxa</S.ValueLabel>
            <S.ValueText>{formatCents(props.feeCents)}</S.ValueText>
          </S.ValueBlock>
        )}
        <S.ValueBlock $end>
          <S.ValueLabel>Total</S.ValueLabel>
          <S.TotalText>{formatCents(props.totalCents)}</S.TotalText>
        </S.ValueBlock>
      </S.Values>
    </S.Card>
  )
}

export interface MobileMetric {
  label: string
  value: string
  hint?: string
  low?: boolean
}

export interface MobileGroupHeaderProps {
  label: string
  summary: string
  metrics?: MobileMetric[]
  issuedPercentage?: number | null
  revenue?: string
  expanded?: boolean
  onToggle?: () => void
  controlsId?: string
}

export function MobileGroupHeader({
  label,
  summary,
  metrics,
  issuedPercentage,
  revenue,
  expanded = false,
  onToggle,
  controlsId,
}: MobileGroupHeaderProps): ReactElement {
  const detailsId = useId()
  const details = (
    <>
      {metrics && metrics.length > 0 ? (
        <S.MetricsGrid>
          {metrics.map((metric) => (
            <S.Metric key={metric.label} title={metric.hint}>
              <S.MetricLabel>{metric.label}</S.MetricLabel>
              <S.MetricValue $low={metric.low === true}>
                {metric.value === '—' && metric.hint ? (
                  <>
                    <span aria-hidden="true">—</span>
                    <S.Hidden>{metric.hint}</S.Hidden>
                  </>
                ) : (
                  metric.value
                )}
              </S.MetricValue>
            </S.Metric>
          ))}
        </S.MetricsGrid>
      ) : (
        <S.SectionSummary>{summary}</S.SectionSummary>
      )}
      {revenue && <S.SectionSummary>{revenue}</S.SectionSummary>}
      {issuedPercentage != null && (
        <S.SectionProgress>
          <ProgressBar
            percentage={issuedPercentage}
            track="accent"
            tone={occupancyTone(issuedPercentage)}
            label={salesReportCopy.issuedShare(formatPercent(issuedPercentage))}
          />
          <S.SectionProgressLabel $tone={occupancyTone(issuedPercentage)}>
            {salesReportCopy.issuedShare(formatPercent(issuedPercentage))}
          </S.SectionProgressLabel>
        </S.SectionProgress>
      )}
    </>
  )

  if (onToggle == null) {
    return (
      <S.SectionHeader>
        <S.SectionName>{label}</S.SectionName>
        {details}
      </S.SectionHeader>
    )
  }

  return (
    <S.SectionCard>
      <S.SectionToggle
        type="button"
        aria-expanded={expanded}
        aria-controls={controlsId}
        aria-describedby={detailsId}
        onClick={onToggle}
      >
        <S.SectionName>{label}</S.SectionName>
        <S.SectionChevron src={salesReportIcons.chevronDown} alt="" width={20} height={20} $open={expanded} />
      </S.SectionToggle>
      <S.SectionDetails id={detailsId}>{details}</S.SectionDetails>
    </S.SectionCard>
  )
}

export interface MobileSubtotalCardProps {
  label: string
  left?: string
  right?: string
  totalLabel: string
  totalCents: number
  feeCents?: number
}

export function MobileSubtotalCard({ label, left, right, totalLabel, totalCents, feeCents }: MobileSubtotalCardProps): ReactElement {
  return (
    <S.SubtotalCard>
      <S.SubtotalTitle>{label}</S.SubtotalTitle>
      {(left || right) && (
        <S.SubtotalSides>
          <span>{left}</span>
          <span>{right}</span>
        </S.SubtotalSides>
      )}
      {feeCents != null && (
        <S.SubtotalSides>
          <span>Taxa:</span>
          <span>{formatCents(feeCents)}</span>
        </S.SubtotalSides>
      )}
      <S.SubtotalTotalRow>
        <S.SubtotalTotalLabel>{totalLabel}</S.SubtotalTotalLabel>
        <S.SubtotalTotalValue>{formatCents(totalCents)}</S.SubtotalTotalValue>
      </S.SubtotalTotalRow>
    </S.SubtotalCard>
  )
}

export interface MobileTotalCardProps {
  label: string
  quantity: number
  totalCents: number
  feeCents?: number
  stockText?: string
}

export function MobileTotalCard({ label, quantity, totalCents, feeCents, stockText }: MobileTotalCardProps): ReactElement {
  return (
    <S.TotalCard role="group" aria-label={label}>
      <S.TotalCardTitle>{label}</S.TotalCardTitle>
      <S.TotalCardSides>
        <span>{`Total Ingressos: ${quantity.toLocaleString('pt-BR')}`}</span>
        <span>
          {'Receita: '}
          <S.TotalCardRevenue>{formatCents(totalCents)}</S.TotalCardRevenue>
        </span>
      </S.TotalCardSides>
      {feeCents != null && <S.TotalCardSides>{`Taxa: ${formatCents(feeCents)}`}</S.TotalCardSides>}
      {stockText && <S.TotalCardSides>{`${salesReportCopy.stockColumn}: ${stockText}`}</S.TotalCardSides>}
    </S.TotalCard>
  )
}
